/**
 * Turning and cropping a photo: the edit the editor builds and the server
 * applies (bloom-backend utils/photoEdit.js), and the small sums behind it.
 *
 * An edit is a quarter turn, clockwise, and an optional crop given as
 * fractions of the turned picture — what the gardener sees after turning it.
 * Turning a cropped picture turns the crop with it, so the box stays round
 * the same part of the photo.
 *
 * An edited photo is stored under its original's name with "_e" and a stamp
 * before the extension; the original is kept beside it. isEditedPhoto reads
 * that from the name, which is how the editor knows to offer "Reset to
 * original" without asking the server.
 *
 * Who uses it: the photo editor (features/plant-list dialogs/photo:
 * ImageEditDialog, ImageEditStage, ImageEditTools, useImageEdit), the bed
 * cover (EditLocationDialog), the photo tray, and api/photos and api/tray,
 * which send an ImageEdit as the request body (PUT …/image).
 *
 * @module imageEdit
 */

import { PHOTO_CROP_MIN_FRACTION, PHOTO_CROP_START_INSET } from '@/config/tunables/shared';

/** A crop, as fractions (0-1) of the turned picture. */
export interface CropBox {
  /** The left edge, from the picture's left. */
  x: number;
  /** The top edge, from the picture's top. */
  y: number;
  /** Across, as a fraction of the picture's width. */
  width: number;
  /** Down, as a fraction of the picture's height. */
  height: number;
}

/** A quarter turn, clockwise, in degrees. */
export type QuarterTurn = 0 | 90 | 180 | 270;

/** What the gardener asked of a picture: a turn, then a crop. */
export interface ImageEdit {
  /** The quarter turn, applied first. */
  rotate: QuarterTurn;
  /** The crop on the turned picture, or null for the whole of it. */
  crop: CropBox | null;
}

/** How a crop box is shaped: a square, or anything. */
export type CropShape = 'square' | 'free';

/** No edit at all. */
export const NO_EDIT: ImageEdit = { rotate: 0, crop: null };

/**
 * Whether an edit would change nothing.
 *
 * @param edit - The edit.
 * @returns True for no turn and no crop.
 */
export const isNoEdit = (edit: ImageEdit): boolean => edit.rotate === 0 && edit.crop === null;

/**
 * Whether a stored photo is an edit of its original (bloom-backend
 * utils/photoEdit.js isEditedName: "_e" and a stamp before the extension).
 * Uploaded names never contain an underscore, so only an edit matches.
 *
 * @param filename - The photo's stored name.
 * @returns True for an edit's name; false for an original, an address, or nothing.
 */
export function isEditedPhoto(filename: string | null | undefined): boolean {
  if (!filename || /^https?:\/\//i.test(filename)) return false;
  const stem = filename.replace(/\.[^/.]+$/, '');
  return /_e[0-9a-z]+$/.test(stem);
}

/**
 * A crop box turned a quarter with its picture.
 *
 * @param crop - The box on the picture before the turn.
 * @param direction - 'right' (clockwise) or 'left'.
 * @returns The same part of the photo, as a box on the turned picture.
 */
export function turnCrop(crop: CropBox, direction: 'left' | 'right'): CropBox {
  return direction === 'right'
    ? { x: 1 - crop.y - crop.height, y: crop.x, width: crop.height, height: crop.width }
    : { x: crop.y, y: 1 - crop.x - crop.width, width: crop.height, height: crop.width };
}

/**
 * An edit turned a quarter further, its crop turned with it.
 *
 * @param edit - The edit so far.
 * @param direction - 'right' (clockwise) or 'left'.
 * @returns The edit with one more quarter turn.
 */
export function turnEdit(edit: ImageEdit, direction: 'left' | 'right'): ImageEdit {
  const rotate = ((edit.rotate + (direction === 'right' ? 90 : 270)) % 360) as QuarterTurn;
  return { rotate, crop: edit.crop ? turnCrop(edit.crop, direction) : null };
}

/**
 * The picture's width over its height once turned.
 *
 * @param naturalWidth - The stored picture's width.
 * @param naturalHeight - Its height.
 * @param rotate - The turn.
 * @returns The aspect ratio; 1 for a picture with no size yet.
 */
export function turnedAspect(naturalWidth: number, naturalHeight: number, rotate: QuarterTurn): number {
  if (!naturalWidth || !naturalHeight) return 1;
  return rotate % 180 === 0 ? naturalWidth / naturalHeight : naturalHeight / naturalWidth;
}

/**
 * A crop box to start from: for a square, the largest square in the middle
 * of the picture; for free, the picture a little in from each edge.
 *
 * @param shape - The box's shape.
 * @param aspect - The turned picture's width over its height.
 * @returns The box.
 */
export function startCrop(shape: CropShape, aspect: number): CropBox {
  if (shape === 'free') {
    const inset = PHOTO_CROP_START_INSET;
    return { x: inset, y: inset, width: 1 - 2 * inset, height: 1 - 2 * inset };
  }
  // A square in pixels is width * aspect = height, as fractions.
  return aspect >= 1
    ? { x: (1 - 1 / aspect) / 2, y: 0, width: 1 / aspect, height: 1 }
    : { x: 0, y: (1 - aspect) / 2, width: 1, height: aspect };
}

/**
 * A crop box made square (in pixels) about its centre, kept inside the
 * picture: what switching to Square does to a box already drawn.
 *
 * @param crop - The box.
 * @param aspect - The turned picture's width over its height.
 * @returns The square box.
 */
export function squareUp(crop: CropBox, aspect: number): CropBox {
  // The side in pixels, measured as a fraction of the picture's height.
  const side = Math.min(crop.width * aspect, crop.height, aspect, 1);
  const width = side / aspect;
  const height = side;
  const cx = crop.x + crop.width / 2;
  const cy = crop.y + crop.height / 2;
  return {
    x: Math.min(1 - width, Math.max(0, cx - width / 2)),
    y: Math.min(1 - height, Math.max(0, cy - height / 2)),
    width,
    height,
  };
}

/** Which part of the box is being dragged: a corner, or the whole box. */
export type CropHandle = 'nw' | 'ne' | 'sw' | 'se' | 'move';

/**
 * The box after a drag: a corner moved (the opposite corner stays put) or
 * the whole box moved, always inside the picture and never smaller than
 * PHOTO_CROP_MIN_FRACTION. A square box stays square in pixels.
 *
 * @param start - The box when the drag began.
 * @param handle - What is being dragged.
 * @param dx - How far the pointer has moved across, as a fraction of the picture's width.
 * @param dy - How far down, as a fraction of its height.
 * @param shape - The box's shape.
 * @param aspect - The turned picture's width over its height.
 * @returns The box after the drag.
 */
export function dragCrop(
  start: CropBox, handle: CropHandle, dx: number, dy: number, shape: CropShape, aspect: number,
): CropBox {
  if (handle === 'move') {
    return {
      ...start,
      x: Math.min(1 - start.width, Math.max(0, start.x + dx)),
      y: Math.min(1 - start.height, Math.max(0, start.y + dy)),
    };
  }
  const min = PHOTO_CROP_MIN_FRACTION;
  const west = handle === 'nw' || handle === 'sw';
  const north = handle === 'nw' || handle === 'ne';
  // The corner that stays put.
  const fixedX = west ? start.x + start.width : start.x;
  const fixedY = north ? start.y + start.height : start.y;
  // The room there is between it and the picture's edges.
  const roomX = west ? fixedX : 1 - fixedX;
  const roomY = north ? fixedY : 1 - fixedY;

  let width = Math.min(roomX, Math.max(min, start.width + (west ? -dx : dx)));
  let height = Math.min(roomY, Math.max(min, start.height + (north ? -dy : dy)));

  if (shape === 'square') {
    // In pixels, measured against the picture's height: width * aspect and height.
    const wanted = Math.max(width * aspect, height);
    const side = Math.max(min * Math.min(aspect, 1), Math.min(wanted, roomX * aspect, roomY));
    width = side / aspect;
    height = side;
  }

  return {
    x: west ? fixedX - width : fixedX,
    y: north ? fixedY - height : fixedY,
    width,
    height,
  };
}
